import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConsentProvider } from './context/ConsentContext';
import ConsentBanner from './components/ConsentBanner';
import SeoManager from './components/SeoManager';
import AuthConversionTracker from './components/AuthConversionTracker';
import LegalPage from './pages/LegalPage';
import NotFound from './pages/NotFound';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { ContactFormProvider } from './context/ContactFormContext';
import LandingPage from './pages/LandingPage';
import Login from './auth/Login';
import Signup from './auth/Signup';
import UserProfile from './pages/UserProfile';
import Dashboard from './components/Dashboard';
import DesktopOnlyGate from './components/DesktopOnlyGate';
import Checkout from './pages/Checkout';
import CheckoutReturn from './pages/CheckoutReturn';

function AuthRetryScreen({ onRetry, reason }) {
  const { theme } = useTheme();
  const message = reason?.includes('API routing returned HTML')
    ? 'The app reached the frontend HTML instead of the API. This usually means the /api rewrite is not active in the deployed frontend.'
    : "We couldn't load your session. This can happen if the server is starting up or the page loaded too quickly.";
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.bg,
      padding: 20,
    }}>
      <div style={{
        maxWidth: 400,
        padding: 32,
        backgroundColor: theme.card,
        borderRadius: 12,
        boxShadow: theme.shadow,
        border: `1px solid ${theme.border}`,
        textAlign: 'center',
      }}>
        <p style={{ margin: '0 0 16px', color: theme.text, fontSize: 16 }}>
          {message}
        </p>
        {reason && (
          <p style={{ margin: '0 0 16px', color: theme.textMuted || theme.text, fontSize: 13, lineHeight: 1.5 }}>
            {reason}
          </p>
        )}
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '10px 20px',
            fontSize: 15,
            fontWeight: 600,
            color: theme.primaryContrast,
            backgroundColor: theme.primary,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

// Protected route component – never redirect to /login from /dashboard; show retry screen instead
function ProtectedRoute({ children }) {
  const location = useLocation();
  const { user, supabaseUser, loading, error, refreshUserProfile, retryAuthOrProfile } = useAuth();
  const isAuthenticated = !!user;
  const hasSessionButNoProfile = !!supabaseUser && !user && !loading;
  const isDashboard = location.pathname?.includes('dashboard');
  const isProfile = location.pathname?.includes('profile');

  if (loading) {
    return <LoadingSpinner />;
  }

  // Session exists but profile fetch failed → retry profile only
  if (hasSessionButNoProfile) {
    return <AuthRetryScreen onRetry={() => refreshUserProfile()} reason={error} />;
  }

  // On dashboard or profile but not authenticated – show retry so they can Try again.
  if ((isDashboard || isProfile) && !isAuthenticated) {
    return <AuthRetryScreen onRetry={retryAuthOrProfile} reason={error} />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

function LoadingSpinner() {
  const { theme } = useTheme();
  const { serverWarmingUp } = useAuth();
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      backgroundColor: theme.bg,
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: `3px solid ${theme.border}`,
        borderTop: `3px solid ${theme.primary}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      {serverWarmingUp && (
        <p style={{
          margin: 0,
          color: theme.textMuted || theme.text,
          fontSize: 14,
          textAlign: 'center',
          maxWidth: 280,
          opacity: 0.8,
        }}>
          Server is starting up — this takes about 15 seconds on first load.
        </p>
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function IntelUnderConstruction() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.bg,
      padding: 20,
    }}>
      <div style={{
        maxWidth: 520,
        padding: 32,
        backgroundColor: theme.card,
        borderRadius: 16,
        boxShadow: theme.shadow,
        border: `1px solid ${theme.border}`,
      }}>
        <h1 style={{ margin: '0 0 12px', color: theme.text, fontSize: 24 }}>Competitive Intel is under construction</h1>
        <p style={{ margin: '0 0 20px', color: theme.textMuted, lineHeight: 1.6 }}>
          This workspace is being prepared as a separate service. The main research dashboard is ready to use while we finish the intel backend rollout.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '11px 18px',
              borderRadius: 10,
              border: 'none',
              backgroundColor: theme.primary,
              color: theme.primaryContrast,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back to dashboard
          </button>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              padding: '11px 18px',
              borderRadius: 10,
              border: `1px solid ${theme.border}`,
              backgroundColor: 'transparent',
              color: theme.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            View profile
          </button>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DesktopOnlyGate>
              <UserProfile />
            </DesktopOnlyGate>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DesktopOnlyGate>
              <Dashboard />
            </DesktopOnlyGate>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/dashboard/intel"
        element={
          <ProtectedRoute>
            <DesktopOnlyGate>
              <IntelUnderConstruction />
            </DesktopOnlyGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/return"
        element={
          <ProtectedRoute>
            <CheckoutReturn />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ConsentProvider>
        <Router>
          <ThemeProvider>
            <ContactFormProvider>
              <SeoManager />
              <AuthConversionTracker />
              <AppRoutes />
              <ConsentBanner />
            </ContactFormProvider>
          </ThemeProvider>
        </Router>
      </ConsentProvider>
    </AuthProvider>
  );
}

export default App;
